// ============================================================================
// PROMPT 24 — Authoritative Registries for Global Expansion & Localization
// ============================================================================

import {
  ScriptDefinition,
  LanguageDefinition,
  LocaleDefinition,
  MarketDefinition,
  CountryPack,
  FinancialConventionPack,
  FinancialTerminologyEntry,
  DoNotTranslateEntry,
  TranslationMessage,
  TranslationUnit,
  TranslationMemoryEntry,
  LocalizedSearchIntent,
  MarketLaunchCandidate,
  MarketLocalizationIncident
} from './globalizationTypes';

export const SCRIPTS_REGISTRY: ScriptDefinition[] = [
  { code: 'Latn', name: 'Latin', nativeName: 'Latin', direction: 'LTR', cldrSupport: true },
  { code: 'Arab', name: 'Arabic', nativeName: 'العربية', direction: 'RTL', cldrSupport: true },
  { code: 'Cyrl', name: 'Cyrillic', nativeName: 'Кириллица', direction: 'LTR', cldrSupport: true },
  { code: 'Hans', name: 'Simplified Han', nativeName: '简体中文', direction: 'LTR', cldrSupport: true },
  { code: 'Hant', name: 'Traditional Han', nativeName: '繁體中文', direction: 'LTR', cldrSupport: true },
  { code: 'Jpan', name: 'Japanese (Kanji/Kana)', nativeName: '日本語', direction: 'LTR', cldrSupport: true }
];

export const LANGUAGES_REGISTRY: LanguageDefinition[] = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    scripts: ['Latn'],
    direction: 'LTR',
    fallbackLanguage: 'en',
    status: 'ACTIVE',
    isSourceLanguage: true
  },
  {
    code: 'fr',
    name: 'French',
    nativeName: 'Français',
    scripts: ['Latn'],
    direction: 'LTR',
    fallbackLanguage: 'en',
    status: 'ACTIVE'
  },
  {
    code: 'de',
    name: 'German',
    nativeName: 'Deutsch',
    scripts: ['Latn'],
    direction: 'LTR',
    fallbackLanguage: 'en',
    status: 'ACTIVE'
  },
  {
    code: 'es',
    name: 'Spanish',
    nativeName: 'Español',
    scripts: ['Latn'],
    direction: 'LTR',
    fallbackLanguage: 'en',
    status: 'ACTIVE'
  },
  {
    code: 'pt',
    name: 'Portuguese',
    nativeName: 'Português',
    scripts: ['Latn'],
    direction: 'LTR',
    fallbackLanguage: 'en',
    status: 'ACTIVE'
  },
  {
    code: 'ar',
    name: 'Arabic',
    nativeName: 'العربية',
    scripts: ['Arab'],
    direction: 'RTL',
    fallbackLanguage: 'en',
    status: 'ACTIVE'
  },
  {
    code: 'ja',
    name: 'Japanese',
    nativeName: '日本語',
    scripts: ['Jpan'],
    direction: 'LTR',
    fallbackLanguage: 'en',
    status: 'QA'
  },
  {
    code: 'zh-Hans',
    name: 'Chinese (Simplified)',
    nativeName: '简体中文',
    scripts: ['Hans'],
    direction: 'LTR',
    fallbackLanguage: 'en',
    status: 'PLANNED'
  }
];

export const LOCALES_REGISTRY: LocaleDefinition[] = [
  {
    bcp47: 'en-US',
    languageCode: 'en',
    countryCode: 'US',
    name: 'English (United States)',
    nativeName: 'English (US)',
    numberingSystem: 'latn',
    decimalSeparator: '.',
    groupingSeparator: ',',
    currencyPlacement: 'PREFIX',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12H',
    weekStart: 0, // Sunday
    textDirection: 'LTR',
    status: 'ACTIVE'
  },
  {
    bcp47: 'en-GB',
    languageCode: 'en',
    countryCode: 'GB',
    name: 'English (United Kingdom)',
    nativeName: 'English (UK)',
    numberingSystem: 'latn',
    decimalSeparator: '.',
    groupingSeparator: ',',
    currencyPlacement: 'PREFIX',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24H',
    weekStart: 1, // Monday
    textDirection: 'LTR',
    status: 'ACTIVE'
  },
  {
    bcp47: 'en-CA',
    languageCode: 'en',
    countryCode: 'CA',
    name: 'English (Canada)',
    nativeName: 'English (Canada)',
    numberingSystem: 'latn',
    decimalSeparator: '.',
    groupingSeparator: ',',
    currencyPlacement: 'PREFIX',
    dateFormat: 'YYYY-MM-DD',
    timeFormat: '12H',
    weekStart: 0,
    textDirection: 'LTR',
    status: 'ACTIVE'
  },
  {
    bcp47: 'fr-FR',
    languageCode: 'fr',
    countryCode: 'FR',
    name: 'French (France)',
    nativeName: 'Français (France)',
    numberingSystem: 'latn',
    decimalSeparator: ',',
    groupingSeparator: ' ',
    currencyPlacement: 'SUFFIX_SPACE',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24H',
    weekStart: 1,
    textDirection: 'LTR',
    status: 'ACTIVE'
  },
  {
    bcp47: 'fr-CA',
    languageCode: 'fr',
    countryCode: 'CA',
    name: 'French (Canada)',
    nativeName: 'Français (Canada)',
    numberingSystem: 'latn',
    decimalSeparator: ',',
    groupingSeparator: ' ',
    currencyPlacement: 'SUFFIX_SPACE',
    dateFormat: 'YYYY-MM-DD',
    timeFormat: '24H',
    weekStart: 0,
    textDirection: 'LTR',
    status: 'ACTIVE'
  },
  {
    bcp47: 'de-DE',
    languageCode: 'de',
    countryCode: 'DE',
    name: 'German (Germany)',
    nativeName: 'Deutsch (Deutschland)',
    numberingSystem: 'latn',
    decimalSeparator: ',',
    groupingSeparator: '.',
    currencyPlacement: 'SUFFIX_SPACE',
    dateFormat: 'DD.MM.YYYY',
    timeFormat: '24H',
    weekStart: 1,
    textDirection: 'LTR',
    status: 'ACTIVE'
  },
  {
    bcp47: 'es-ES',
    languageCode: 'es',
    countryCode: 'ES',
    name: 'Spanish (Spain)',
    nativeName: 'Español (España)',
    numberingSystem: 'latn',
    decimalSeparator: ',',
    groupingSeparator: '.',
    currencyPlacement: 'SUFFIX_SPACE',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24H',
    weekStart: 1,
    textDirection: 'LTR',
    status: 'ACTIVE'
  },
  {
    bcp47: 'pt-BR',
    languageCode: 'pt',
    countryCode: 'BR',
    name: 'Portuguese (Brazil)',
    nativeName: 'Português (Brasil)',
    numberingSystem: 'latn',
    decimalSeparator: ',',
    groupingSeparator: '.',
    currencyPlacement: 'PREFIX_SPACE',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24H',
    weekStart: 0,
    textDirection: 'LTR',
    status: 'ACTIVE'
  },
  {
    bcp47: 'ar-SA',
    languageCode: 'ar',
    countryCode: 'SA',
    name: 'Arabic (Saudi Arabia)',
    nativeName: 'العربية (المملكة العربية السعودية)',
    numberingSystem: 'arab', // or latn in modern financial UI
    decimalSeparator: '٫',
    groupingSeparator: '٬',
    currencyPlacement: 'PREFIX_SPACE',
    dateFormat: 'YYYY-MM-DD',
    timeFormat: '12H',
    weekStart: 0,
    textDirection: 'RTL',
    status: 'ACTIVE'
  },
  {
    bcp47: 'fr-CM',
    languageCode: 'fr',
    countryCode: 'CM',
    name: 'French (Cameroon)',
    nativeName: 'Français (Cameroun)',
    numberingSystem: 'latn',
    decimalSeparator: ',',
    groupingSeparator: ' ',
    currencyPlacement: 'SUFFIX_SPACE',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24H',
    weekStart: 1,
    textDirection: 'LTR',
    status: 'ACTIVE'
  },
  {
    bcp47: 'en-NG',
    languageCode: 'en',
    countryCode: 'NG',
    name: 'English (Nigeria)',
    nativeName: 'English (Nigeria)',
    numberingSystem: 'latn',
    decimalSeparator: '.',
    groupingSeparator: ',',
    currencyPlacement: 'PREFIX',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12H',
    weekStart: 1,
    textDirection: 'LTR',
    status: 'ACTIVE'
  },
  {
    bcp47: 'ja-JP',
    languageCode: 'ja',
    countryCode: 'JP',
    name: 'Japanese (Japan)',
    nativeName: '日本語 (日本)',
    numberingSystem: 'latn',
    decimalSeparator: '.',
    groupingSeparator: ',',
    currencyPlacement: 'PREFIX',
    dateFormat: 'YYYY/MM/DD',
    timeFormat: '24H',
    weekStart: 0,
    textDirection: 'LTR',
    status: 'QA'
  }
];

export const FINANCIAL_CONVENTIONS: FinancialConventionPack[] = [
  {
    id: 'FC-US',
    jurisdictionCode: 'US',
    name: 'US Financial Conventions',
    defaultCurrency: 'USD',
    currencyPlacement: 'PREFIX',
    percentageFormat: '0.00%',
    taxYearStart: '01-01',
    standardPayPeriods: ['ANNUAL', 'BI_WEEKLY', 'MONTHLY', 'WEEKLY'],
    interestConvention: 'APR',
    mortgageTerminology: {
      principal: 'Loan Principal',
      downPayment: 'Down Payment',
      amortization: 'Amortization Schedule',
      escrow: 'Property Tax & Insurance Escrow',
      pmi: 'Private Mortgage Insurance (PMI)'
    },
    salaryTerminology: {
      gross: 'Gross Annual Pay',
      net: 'Take-Home Net Pay',
      withholding: 'Federal & State Withholding',
      socialContributions: 'FICA (OASDI 6.2% + Medicare 1.45%)'
    },
    units: {
      fuel: 'MPG_US',
      weight: 'LB',
      area: 'SQFT'
    }
  },
  {
    id: 'FC-GB',
    jurisdictionCode: 'GB',
    name: 'UK Financial Conventions',
    defaultCurrency: 'GBP',
    currencyPlacement: 'PREFIX',
    percentageFormat: '0.00%',
    taxYearStart: '04-06', // 6 April
    standardPayPeriods: ['ANNUAL', 'MONTHLY', 'WEEKLY'],
    interestConvention: 'APRC',
    mortgageTerminology: {
      principal: 'Mortgage Advance',
      downPayment: 'Deposit',
      amortization: 'Repayment Schedule'
    },
    salaryTerminology: {
      gross: 'Gross Salary',
      net: 'Take-Home Pay',
      withholding: 'PAYE Income Tax',
      socialContributions: 'National Insurance (Class 1 NIC)'
    },
    units: {
      fuel: 'MPG_UK',
      weight: 'KG',
      area: 'SQFT'
    }
  },
  {
    id: 'FC-FR',
    jurisdictionCode: 'FR',
    name: 'French Financial Conventions',
    defaultCurrency: 'EUR',
    currencyPlacement: 'SUFFIX',
    percentageFormat: '0,00 %',
    taxYearStart: '01-01',
    standardPayPeriods: ['MONTHLY', 'ANNUAL'],
    interestConvention: 'TAEG',
    mortgageTerminology: {
      principal: 'Capital Emprunté',
      downPayment: 'Apport Personnel',
      amortization: 'Tableau d\'Amortissement'
    },
    salaryTerminology: {
      gross: 'Salaire Brut',
      net: 'Salaire Net à Payer (Avant PAS)',
      withholding: 'Prélèvement à la Source (PAS)',
      socialContributions: 'Cotisations Sociales Salariales (CSG/CRDS)'
    },
    units: {
      fuel: 'L_PER_100KM',
      weight: 'KG',
      area: 'SQM'
    }
  },
  {
    id: 'FC-CA',
    jurisdictionCode: 'CA',
    name: 'Canadian Financial Conventions',
    defaultCurrency: 'CAD',
    currencyPlacement: 'PREFIX',
    percentageFormat: '0.00%',
    taxYearStart: '01-01',
    standardPayPeriods: ['ANNUAL', 'BI_WEEKLY', 'MONTHLY', 'WEEKLY'],
    interestConvention: 'APR',
    mortgageTerminology: {
      principal: 'Mortgage Principal',
      downPayment: 'Down Payment',
      amortization: 'Amortization Period (vs Term)'
    },
    salaryTerminology: {
      gross: 'Gross Income',
      net: 'Net Paycheque',
      withholding: 'Federal & Provincial Tax',
      socialContributions: 'CPP/QPP & EI Premiums'
    },
    units: {
      fuel: 'L_PER_100KM',
      weight: 'KG',
      area: 'SQFT'
    }
  },
  {
    id: 'FC-CM',
    jurisdictionCode: 'CM',
    name: 'Cameroon (CEMAC / OHADA) Conventions',
    defaultCurrency: 'XAF',
    currencyPlacement: 'SUFFIX',
    percentageFormat: '0,00 %',
    taxYearStart: '01-01',
    standardPayPeriods: ['MONTHLY', 'ANNUAL'],
    interestConvention: 'NOMINAL',
    mortgageTerminology: {
      principal: 'Montant du Prêt Immobilier',
      downPayment: 'Apport Initial',
      amortization: 'Échéancier de Remboursement'
    },
    salaryTerminology: {
      gross: 'Salaire Brut Global',
      net: 'Net à Percevoir',
      withholding: 'IRPP & Centimes Additionnels',
      socialContributions: 'Cotisation CNPS (Pensions de Vieillesse)'
    },
    units: {
      fuel: 'L_PER_100KM',
      weight: 'KG',
      area: 'SQM'
    }
  },
  {
    id: 'FC-SA',
    jurisdictionCode: 'SA',
    name: 'Saudi Arabia / GCC Conventions',
    defaultCurrency: 'SAR',
    currencyPlacement: 'PREFIX',
    percentageFormat: '0.00%',
    taxYearStart: '01-01',
    standardPayPeriods: ['MONTHLY', 'ANNUAL'],
    interestConvention: 'NOMINAL',
    mortgageTerminology: {
      principal: 'مبلغ التمويل العقاري (Principal)',
      downPayment: 'الدفعة الأولى (Down Payment)',
      amortization: 'جدول السداد (Amortization)'
    },
    salaryTerminology: {
      gross: 'الراتب الإجمالي (Gross)',
      net: 'صافي الراتب (Net)',
      withholding: 'الاستقطاع الضريبي (Withholding)',
      socialContributions: 'التأمينات الاجتماعية (GOSI)'
    },
    units: {
      fuel: 'KM_PER_L',
      weight: 'KG',
      area: 'SQM'
    }
  }
];

export const COUNTRY_PACKS_REGISTRY: CountryPack[] = [
  {
    countryCode: 'US',
    name: 'United States',
    nativeName: 'United States',
    region: 'North America',
    subdivisions: [
      { code: 'CA', name: 'California', taxRateOrRuleNote: 'Bracketed up to 13.3%' },
      { code: 'NY', name: 'New York', taxRateOrRuleNote: 'State + NYC local tax' },
      { code: 'TX', name: 'Texas', taxRateOrRuleNote: '0% State Income Tax' },
      { code: 'FL', name: 'Florida', taxRateOrRuleNote: '0% State Income Tax' },
      { code: 'WA', name: 'Washington', taxRateOrRuleNote: '0% State Income Tax' }
    ],
    officialLanguages: ['en'],
    locales: ['en-US'],
    currencies: ['USD'],
    defaultCurrency: 'USD',
    callingCode: '+1',
    financialConventionPackId: 'FC-US',
    version: '2026.8.0',
    lastUpdated: '2026-08-20',
    legalEntityPresent: true,
    paymentMethodsAvailable: ['CARD', 'ACH', 'APPLE_PAY', 'GOOGLE_PAY'],
    activeRuleEngines: ['US_IRS_PUB15T', 'CFPB_TILA_AMORTIZATION', 'FICA_SOCIAL_SECURITY']
  },
  {
    countryCode: 'GB',
    name: 'United Kingdom',
    nativeName: 'United Kingdom',
    region: 'Europe',
    subdivisions: [
      { code: 'ENG', name: 'England' },
      { code: 'SCT', name: 'Scotland', taxRateOrRuleNote: 'Scottish Income Tax Brackets (19%-48%)' },
      { code: 'WLS', name: 'Wales' },
      { code: 'NIR', name: 'Northern Ireland' }
    ],
    officialLanguages: ['en'],
    locales: ['en-GB'],
    currencies: ['GBP'],
    defaultCurrency: 'GBP',
    callingCode: '+44',
    financialConventionPackId: 'FC-GB',
    version: '2026.4.1',
    lastUpdated: '2026-08-15',
    legalEntityPresent: true,
    paymentMethodsAvailable: ['CARD', 'BACS', 'APPLE_PAY', 'GOOGLE_PAY'],
    activeRuleEngines: ['UK_HMRC_PAYE', 'UK_CLASS1_NIC', 'UK_STUDENT_LOAN_PLANS']
  },
  {
    countryCode: 'FR',
    name: 'France',
    nativeName: 'France',
    region: 'Europe',
    subdivisions: [
      { code: 'IDF', name: 'Île-de-France' },
      { code: 'ARA', name: 'Auvergne-Rhône-Alpes' },
      { code: 'PAC', name: 'Provence-Alpes-Côte d\'Azur' }
    ],
    officialLanguages: ['fr'],
    locales: ['fr-FR'],
    currencies: ['EUR'],
    defaultCurrency: 'EUR',
    callingCode: '+33',
    financialConventionPackId: 'FC-FR',
    version: '2026.2.0',
    lastUpdated: '2026-08-10',
    legalEntityPresent: true,
    paymentMethodsAvailable: ['CARD', 'SEPA', 'APPLE_PAY', 'GOOGLE_PAY'],
    activeRuleEngines: ['FR_DGFIP_CGI197', 'FR_URSSAF_COTISATIONS', 'FR_PAS_WITHHOLDING']
  },
  {
    countryCode: 'CA',
    name: 'Canada',
    nativeName: 'Canada',
    region: 'North America',
    subdivisions: [
      { code: 'ON', name: 'Ontario' },
      { code: 'QC', name: 'Quebec', taxRateOrRuleNote: 'Separate Revenu Québec TP-1 rules' },
      { code: 'BC', name: 'British Columbia' },
      { code: 'AB', name: 'Alberta' }
    ],
    officialLanguages: ['en', 'fr'],
    locales: ['en-CA', 'fr-CA'],
    currencies: ['CAD'],
    defaultCurrency: 'CAD',
    callingCode: '+1',
    financialConventionPackId: 'FC-CA',
    version: '2026.5.0',
    lastUpdated: '2026-08-12',
    legalEntityPresent: true,
    paymentMethodsAvailable: ['CARD', 'INTERAC_ONLINE', 'APPLE_PAY', 'GOOGLE_PAY'],
    activeRuleEngines: ['CA_CRA_T4032', 'CA_RQ_TP1015', 'CA_CPP_EI_PREMIUMS']
  },
  {
    countryCode: 'DE',
    name: 'Germany',
    nativeName: 'Deutschland',
    region: 'Europe',
    subdivisions: [
      { code: 'BY', name: 'Bavaria' },
      { code: 'NW', name: 'North Rhine-Westphalia' },
      { code: 'BE', name: 'Berlin' }
    ],
    officialLanguages: ['de'],
    locales: ['de-DE'],
    currencies: ['EUR'],
    defaultCurrency: 'EUR',
    callingCode: '+49',
    financialConventionPackId: 'FC-FR', // Reuses EUR base convention with DE rules
    version: '2026.3.0',
    lastUpdated: '2026-08-01',
    legalEntityPresent: true,
    paymentMethodsAvailable: ['CARD', 'SEPA', 'SOFORT', 'GIROPAY'],
    activeRuleEngines: ['DE_EINKOMMENSTEUER_ESTG', 'DE_SOZIALVERSICHERUNG']
  },
  {
    countryCode: 'CM',
    name: 'Cameroon',
    nativeName: 'Cameroun',
    region: 'Africa',
    subdivisions: [
      { code: 'CE', name: 'Centre (Yaoundé)' },
      { code: 'LT', name: 'Littoral (Douala)' },
      { code: 'OU', name: 'Ouest' }
    ],
    officialLanguages: ['fr', 'en'],
    locales: ['fr-CM', 'en-CM'],
    currencies: ['XAF'],
    defaultCurrency: 'XAF',
    callingCode: '+237',
    financialConventionPackId: 'FC-CM',
    version: '2026.1.0',
    lastUpdated: '2026-07-28',
    legalEntityPresent: false,
    paymentMethodsAvailable: ['MTN_MOMO', 'ORANGE_MONEY', 'CARD'],
    activeRuleEngines: ['CM_CGI_IRPP', 'CM_CNPS_PENSIONS']
  },
  {
    countryCode: 'NG',
    name: 'Nigeria',
    nativeName: 'Nigeria',
    region: 'Africa',
    subdivisions: [
      { code: 'LA', name: 'Lagos' },
      { code: 'FC', name: 'Abuja FCT' },
      { code: 'RI', name: 'Rivers' }
    ],
    officialLanguages: ['en'],
    locales: ['en-NG'],
    currencies: ['NGN'],
    defaultCurrency: 'NGN',
    callingCode: '+234',
    financialConventionPackId: 'FC-US', // Base NGN with US-like formatting
    version: '2026.2.0',
    lastUpdated: '2026-08-02',
    legalEntityPresent: false,
    paymentMethodsAvailable: ['CARD', 'BANK_TRANSFER', 'PAYSTACK', 'FLUTTERWAVE'],
    activeRuleEngines: ['NG_PITA_PAYE', 'NG_PENSION_REFORM_ACT']
  },
  {
    countryCode: 'SA',
    name: 'Saudi Arabia',
    nativeName: 'المملكة العربية السعودية',
    region: 'Middle East',
    subdivisions: [
      { code: 'RI', name: 'Riyadh' },
      { code: 'MK', name: 'Makkah' },
      { code: 'EP', name: 'Eastern Province' }
    ],
    officialLanguages: ['ar'],
    locales: ['ar-SA'],
    currencies: ['SAR'],
    defaultCurrency: 'SAR',
    callingCode: '+966',
    financialConventionPackId: 'FC-SA',
    version: '2026.4.0',
    lastUpdated: '2026-08-18',
    legalEntityPresent: false,
    paymentMethodsAvailable: ['MADA', 'CARD', 'APPLE_PAY', 'STC_PAY'],
    activeRuleEngines: ['SA_GOSI_CONTRIBUTIONS', 'SA_ZAKAT_VAT']
  }
];

export const MARKETS_REGISTRY: MarketDefinition[] = [
  {
    marketCode: 'US',
    name: 'United States',
    countryCode: 'US',
    region: 'North America',
    status: 'ACTIVE_PAID',
    launchWave: 'WAVE_1_NORTH_AMERICA',
    defaultLocale: 'en-US',
    supportedLocales: ['en-US', 'es-ES'],
    defaultCurrency: 'USD',
    supportedCurrencies: ['USD'],
    jurisdictionPackId: 'JUR-US-FED',
    financialConventionPackId: 'FC-US',
    countryPackVersion: '2026.8.0',
    paidMonetizationEnabled: true,
    complianceState: 'PASSED',
    qualityCertificationId: 'REL-2026-US-P23',
    owner: 'Chief Global Expansion Officer',
    opportunityScore: 98,
    launchDate: '2026-01-15',
    productCoverage: [
      { toolId: 'salary-engine', toolName: 'Paycheck & 401(k) Calculator', category: 'SALARY', coverage: 'FULLY_LOCALIZED', ruleSetVersion: 'IRS-2026-P15T' },
      { toolId: 'loan-engine', toolName: 'Mortgage Amortization & TILA', category: 'LOAN', coverage: 'FULLY_LOCALIZED', ruleSetVersion: 'CFPB-TILA-2026' },
      { toolId: 'currency-intelligence', toolName: 'FX Triangulation Engine', category: 'CURRENCY', coverage: 'FULLY_LOCALIZED' },
      { toolId: 'money-transfers', toolName: 'Remittance Corridor Intelligence', category: 'CURRENCY', coverage: 'FULLY_LOCALIZED' },
      { toolId: 'wealth-command-center', toolName: 'FIRE & Compound Wealth Engine', category: 'SAVINGS', coverage: 'FULLY_LOCALIZED' },
      { toolId: 'everyday-command-center', toolName: 'Everyday Calculator Hub', category: 'EVERYDAY', coverage: 'GLOBAL_GENERIC' },
      { toolId: 'billing-command-center', toolName: 'Stripe Direct Billing', category: 'BILLING', coverage: 'FULLY_LOCALIZED' }
    ]
  },
  {
    marketCode: 'UK',
    name: 'United Kingdom',
    countryCode: 'GB',
    region: 'Europe',
    status: 'ACTIVE_PAID',
    launchWave: 'WAVE_2_EUROPE',
    defaultLocale: 'en-GB',
    supportedLocales: ['en-GB'],
    defaultCurrency: 'GBP',
    supportedCurrencies: ['GBP', 'EUR', 'USD'],
    jurisdictionPackId: 'JUR-GB',
    financialConventionPackId: 'FC-GB',
    countryPackVersion: '2026.4.1',
    paidMonetizationEnabled: true,
    complianceState: 'PASSED',
    qualityCertificationId: 'REL-2026-UK-P23',
    owner: 'Regional Product Lead - EMEA',
    opportunityScore: 94,
    launchDate: '2026-03-01',
    productCoverage: [
      { toolId: 'salary-engine', toolName: 'PAYE & NIC Take-Home Pay', category: 'SALARY', coverage: 'FULLY_LOCALIZED', ruleSetVersion: 'HMRC-2026-PAYE' },
      { toolId: 'loan-engine', toolName: 'UK Mortgage Repayment & Stamp Duty', category: 'LOAN', coverage: 'FULLY_LOCALIZED' },
      { toolId: 'currency-intelligence', toolName: 'FX Triangulation Engine', category: 'CURRENCY', coverage: 'FULLY_LOCALIZED' },
      { toolId: 'billing-command-center', toolName: 'Stripe UK Billing', category: 'BILLING', coverage: 'FULLY_LOCALIZED' }
    ]
  },
  {
    marketCode: 'FR',
    name: 'France',
    countryCode: 'FR',
    region: 'Europe',
    status: 'ACTIVE_PAID',
    launchWave: 'WAVE_2_EUROPE',
    defaultLocale: 'fr-FR',
    supportedLocales: ['fr-FR', 'en-US'],
    defaultCurrency: 'EUR',
    supportedCurrencies: ['EUR', 'USD'],
    jurisdictionPackId: 'JUR-FR',
    financialConventionPackId: 'FC-FR',
    countryPackVersion: '2026.2.0',
    paidMonetizationEnabled: true,
    complianceState: 'PASSED',
    qualityCertificationId: 'REL-2026-FR-P23',
    owner: 'Regional Product Lead - EMEA',
    opportunityScore: 91,
    launchDate: '2026-04-10',
    productCoverage: [
      { toolId: 'salary-engine', toolName: 'Calculateur Brut / Net & PAS', category: 'SALARY', coverage: 'FULLY_LOCALIZED', ruleSetVersion: 'DGFIP-2026-CGI197' },
      { toolId: 'loan-engine', toolName: 'Crédit Immobilier & TAEG', category: 'LOAN', coverage: 'FULLY_LOCALIZED' },
      { toolId: 'currency-intelligence', toolName: 'Intelligence Devises BCE', category: 'CURRENCY', coverage: 'FULLY_LOCALIZED' },
      { toolId: 'billing-command-center', toolName: 'Facturation SEPA & Carte', category: 'BILLING', coverage: 'FULLY_LOCALIZED' }
    ]
  },
  {
    marketCode: 'CA',
    name: 'Canada',
    countryCode: 'CA',
    region: 'North America',
    status: 'ACTIVE_PAID',
    launchWave: 'WAVE_1_NORTH_AMERICA',
    defaultLocale: 'en-CA',
    supportedLocales: ['en-CA', 'fr-CA'],
    defaultCurrency: 'CAD',
    supportedCurrencies: ['CAD', 'USD'],
    jurisdictionPackId: 'JUR-CA-FED',
    financialConventionPackId: 'FC-CA',
    countryPackVersion: '2026.5.0',
    paidMonetizationEnabled: true,
    complianceState: 'PASSED',
    qualityCertificationId: 'REL-2026-CA-P23',
    owner: 'Chief Global Expansion Officer',
    opportunityScore: 89,
    launchDate: '2026-02-20',
    productCoverage: [
      { toolId: 'salary-engine', toolName: 'CRA T4032 & Québec TP-1 Pay Calculator', category: 'SALARY', coverage: 'FULLY_LOCALIZED', ruleSetVersion: 'CRA-2026-T4032' },
      { toolId: 'loan-engine', toolName: 'Canadian Mortgage Stress Test & Amortization', category: 'LOAN', coverage: 'FULLY_LOCALIZED' }
    ]
  },
  {
    marketCode: 'DE',
    name: 'Germany',
    countryCode: 'DE',
    region: 'Europe',
    status: 'ACTIVE_FREE',
    launchWave: 'WAVE_2_EUROPE',
    defaultLocale: 'de-DE',
    supportedLocales: ['de-DE', 'en-US'],
    defaultCurrency: 'EUR',
    supportedCurrencies: ['EUR'],
    jurisdictionPackId: 'JUR-DE',
    financialConventionPackId: 'FC-FR',
    countryPackVersion: '2026.3.0',
    paidMonetizationEnabled: false,
    complianceState: 'PASSED',
    owner: 'Regional Product Lead - EMEA',
    opportunityScore: 92,
    productCoverage: [
      { toolId: 'salary-engine', toolName: 'Brutto-Netto-Rechner EStG', category: 'SALARY', coverage: 'LOCALIZED', ruleSetVersion: 'BMF-2026-ESTG' },
      { toolId: 'currency-intelligence', toolName: 'Währungsrechner', category: 'CURRENCY', coverage: 'FULLY_LOCALIZED' }
    ]
  },
  {
    marketCode: 'CM',
    name: 'Cameroon',
    countryCode: 'CM',
    region: 'Africa',
    status: 'ACTIVE_FREE',
    launchWave: 'WAVE_5_AFRICA',
    defaultLocale: 'fr-CM',
    supportedLocales: ['fr-CM', 'en-CM'],
    defaultCurrency: 'XAF',
    supportedCurrencies: ['XAF', 'EUR', 'USD'],
    jurisdictionPackId: 'JUR-CM',
    financialConventionPackId: 'FC-CM',
    countryPackVersion: '2026.1.0',
    paidMonetizationEnabled: false,
    complianceState: 'PASSED',
    owner: 'Regional Lead - Africa',
    opportunityScore: 78,
    launchDate: '2026-06-01',
    productCoverage: [
      { toolId: 'salary-engine', toolName: 'Calculateur Salaire Brut Net Cameroun (IRPP/CNPS)', category: 'SALARY', coverage: 'FULLY_LOCALIZED', ruleSetVersion: 'CM-CGI-2026' },
      { toolId: 'money-transfers', toolName: 'Corridor Remittances France/USA -> Cameroun', category: 'CURRENCY', coverage: 'FULLY_LOCALIZED' },
      { toolId: 'currency-intelligence', toolName: 'Devises XAF / BEAC Intelligence', category: 'CURRENCY', coverage: 'FULLY_LOCALIZED' }
    ]
  },
  {
    marketCode: 'NG',
    name: 'Nigeria',
    countryCode: 'NG',
    region: 'Africa',
    status: 'ACTIVE_FREE',
    launchWave: 'WAVE_5_AFRICA',
    defaultLocale: 'en-NG',
    supportedLocales: ['en-NG'],
    defaultCurrency: 'NGN',
    supportedCurrencies: ['NGN', 'USD', 'GBP'],
    jurisdictionPackId: 'JUR-NG',
    financialConventionPackId: 'FC-US',
    countryPackVersion: '2026.2.0',
    paidMonetizationEnabled: false,
    complianceState: 'PASSED',
    owner: 'Regional Lead - Africa',
    opportunityScore: 84,
    launchDate: '2026-07-15',
    productCoverage: [
      { toolId: 'salary-engine', toolName: 'Nigerian PAYE & Pension Calculator', category: 'SALARY', coverage: 'LOCALIZED', ruleSetVersion: 'NG-PITA-2026' },
      { toolId: 'money-transfers', toolName: 'Diaspora Remittance Corridor (UK/US -> Nigeria)', category: 'CURRENCY', coverage: 'FULLY_LOCALIZED' }
    ]
  },
  {
    marketCode: 'SA',
    name: 'Saudi Arabia',
    countryCode: 'SA',
    region: 'Middle East',
    status: 'LIMITED',
    launchWave: 'WAVE_4_ME_LATAM',
    defaultLocale: 'ar-SA',
    supportedLocales: ['ar-SA', 'en-US'],
    defaultCurrency: 'SAR',
    supportedCurrencies: ['SAR', 'USD'],
    jurisdictionPackId: 'JUR-SA',
    financialConventionPackId: 'FC-SA',
    countryPackVersion: '2026.4.0',
    paidMonetizationEnabled: false,
    complianceState: 'PENDING',
    owner: 'Principal RTL & ME Product Architect',
    opportunityScore: 88,
    productCoverage: [
      { toolId: 'salary-engine', toolName: 'حاسبة الرواتب والتأمينات الاجتماعية (GOSI)', category: 'SALARY', coverage: 'LOCALIZED' },
      { toolId: 'currency-intelligence', toolName: 'أسعار صرف الريال السعودي والعملات العالمية', category: 'CURRENCY', coverage: 'FULLY_LOCALIZED' }
    ]
  }
];

export const FINANCIAL_TERMINOLOGY_REGISTRY: FinancialTerminologyEntry[] = [
  {
    id: 'TERM-001',
    conceptKey: 'GROSS_SALARY',
    domain: 'SALARY',
    sourceTermEn: 'Gross Salary',
    approvedTermsByLocale: {
      'en-US': 'Gross Annual Pay',
      'en-GB': 'Gross Salary',
      'en-CA': 'Gross Income',
      'fr-FR': 'Salaire Brut',
      'fr-CA': 'Salaire Brut',
      'fr-CM': 'Salaire Brut Global',
      'de-DE': 'Bruttogehalt',
      'es-ES': 'Salario Bruto',
      'pt-BR': 'Salário Bruto',
      'ar-SA': 'الراتب الإجمالي'
    },
    forbiddenTerms: {
      'fr-FR': ['Salaire Total', 'Paiement Brut'],
      'de-DE': ['Gesamtgehalt (wenn Netto unklar)'],
      'es-ES': ['Paga Total']
    },
    definition: 'Total salary compensation earned before deductions, taxes, and social security withholdings.',
    contextNote: 'Must never be translated simply as "total pay" because statutory definitions distinguish between total compensation vs taxable gross base.',
    jurisdictionSpecific: false,
    reviewedBy: 'Senior Financial Localization Specialist',
    updatedAt: '2026-08-20'
  },
  {
    id: 'TERM-002',
    conceptKey: 'NET_TAKE_HOME_PAY',
    domain: 'SALARY',
    sourceTermEn: 'Net Take-Home Pay',
    approvedTermsByLocale: {
      'en-US': 'Take-Home Pay',
      'en-GB': 'Take-Home Pay',
      'en-CA': 'Net Paycheque',
      'fr-FR': 'Salaire Net à Payer',
      'fr-CA': 'Paie Nette',
      'fr-CM': 'Net à Percevoir',
      'de-DE': 'Nettogehalt (Auszahlungsbetrag)',
      'es-ES': 'Salario Neto',
      'pt-BR': 'Salário Líquido',
      'ar-SA': 'صافي الراتب'
    },
    forbiddenTerms: {
      'fr-FR': ['Net Fiscal (diffère du net à payer)', 'Reste en main'],
      'en-US': ['Gross Pocket']
    },
    definition: 'Actual cash amount received by employee into bank account after statutory tax withholdings and employee benefit deductions.',
    contextNote: 'In France, distinguish strictly between Net Fiscal vs Net à Payer avant PAS vs Net après PAS.',
    jurisdictionSpecific: true,
    reviewedBy: 'Tax Localization Specialist',
    updatedAt: '2026-08-22'
  },
  {
    id: 'TERM-003',
    conceptKey: 'APR_LOAN_INTEREST',
    domain: 'LOAN',
    sourceTermEn: 'Annual Percentage Rate (APR)',
    approvedTermsByLocale: {
      'en-US': 'Annual Percentage Rate (APR)',
      'en-GB': 'Annual Percentage Rate of Charge (APRC)',
      'en-CA': 'Annual Percentage Rate (APR)',
      'fr-FR': 'Taux Annuel Effectif Global (TAEG)',
      'fr-CA': 'Taux Effectif Annuel (TEA)',
      'fr-CM': 'Taux Effectif Global (TEG)',
      'de-DE': 'Effektiver Jahreszins',
      'es-ES': 'Tasa Anual Equivalente (TAE)',
      'pt-BR': 'Taxa Efetiva Anual',
      'ar-SA': 'معدل النسبة السنوي (APR)'
    },
    forbiddenTerms: {
      'fr-FR': ['Taux Nominal (trompeur sans frais)', 'APR'],
      'de-DE': ['Nominalzins (ohne Nebenkosten)']
    },
    definition: 'Standardized measure of cost of borrowing expressed as a yearly rate including interest, origination fees, and required closing costs.',
    contextNote: 'Regulatory compliance mandates using the exact statutory term (TAEG in France/EU, APR in US, TEA in Quebec, TAE in Spain).',
    jurisdictionSpecific: true,
    reviewedBy: 'Senior Compliance Operations Specialist',
    updatedAt: '2026-08-18'
  },
  {
    id: 'TERM-004',
    conceptKey: 'DOWN_PAYMENT',
    domain: 'LOAN',
    sourceTermEn: 'Down Payment',
    approvedTermsByLocale: {
      'en-US': 'Down Payment',
      'en-GB': 'Deposit',
      'en-CA': 'Down Payment / Mise de Fonds',
      'fr-FR': 'Apport Personnel',
      'fr-CA': 'Mise de Fonds',
      'fr-CM': 'Apport Initial',
      'de-DE': 'Eigenkapital',
      'es-ES': 'Entrada / Aportación Inicial',
      'pt-BR': 'Entrada',
      'ar-SA': 'الدفعة الأولى'
    },
    forbiddenTerms: {
      'en-GB': ['Down Payment (Americanism in UK mortgages)'],
      'fr-FR': ['Dépôt (ambigu avec caution locative)']
    },
    definition: 'Initial upfront portion of the total purchase price paid directly by the buyer in cash at mortgage closing.',
    contextNote: 'In the UK, "Deposit" is mandatory. In France, "Apport personnel" is mandatory to prevent confusion with rent security deposits.',
    jurisdictionSpecific: true,
    reviewedBy: 'Principal Global UX Architect',
    updatedAt: '2026-08-15'
  }
];

export const DO_NOT_TRANSLATE_REGISTRY: DoNotTranslateEntry[] = [
  { term: 'YamooPay', category: 'BRAND', reason: 'Global trademark and corporate brand name.' },
  { term: 'YamooPay Core', category: 'BRAND', reason: 'Platform architecture identifier.' },
  { term: 'Stripe', category: 'PROVIDER', reason: 'Licensed payment processor partner brand.' },
  { term: 'Plaid', category: 'PROVIDER', reason: 'Open banking data provider brand.' },
  { term: 'ISO 4217', category: 'ACRONYM', reason: 'International currency coding standard.' },
  { term: 'BCP 47', category: 'ACRONYM', reason: 'IETF language tag specification.' },
  { term: 'Unicode CLDR', category: 'ACRONYM', reason: 'Common Locale Data Repository.' },
  { term: 'IRS Pub 15-T', category: 'LEGAL_ENTITY', reason: 'US federal statutory tax schedule publication code.' },
  { term: 'HMRC PAYE', category: 'LEGAL_ENTITY', reason: 'UK statutory tax system identifier.' },
  { term: 'DGFiP CGI', category: 'LEGAL_ENTITY', reason: 'Code Général des Impôts authority in France.' },
  { term: 'CRA T4032', category: 'LEGAL_ENTITY', reason: 'Canada Revenue Agency payroll tables guide.' },
  { term: 'OHADA', category: 'LEGAL_ENTITY', reason: 'Organisation pour l\'Harmonisation en Afrique du Droit des Affaires.' },
  { term: 'API v1', category: 'API_NAME', reason: 'Public API contract route designation.' },
  { term: 'JSON-LD', category: 'ACRONYM', reason: 'Semantic web schema markup standard.' }
];

export const SAMPLE_TRANSLATION_MESSAGES: TranslationMessage[] = [
  {
    key: 'nav.home',
    namespace: 'nav',
    sourceTextEn: 'Home',
    description: 'Main navigation home link',
    context: 'Top navigation bar',
    variables: [],
    sensitivity: 'STANDARD',
    owner: 'UX Team',
    sourceHash: 'h_nav_home_1'
  },
  {
    key: 'calculator.salary.title',
    namespace: 'salary',
    sourceTextEn: 'Salary & Take-Home Pay Calculator',
    description: 'Heading for statutory salary computation tool',
    context: 'Salary engine header',
    variables: [],
    sensitivity: 'FINANCIAL',
    owner: 'Financial Core Team',
    sourceHash: 'h_sal_title_1'
  },
  {
    key: 'calculator.salary.grossLabel',
    namespace: 'salary',
    sourceTextEn: 'Gross Annual Pay',
    description: 'Input label for annual gross compensation before taxes',
    context: 'Salary engine input form',
    variables: [],
    sensitivity: 'FINANCIAL',
    owner: 'Financial Core Team',
    sourceHash: 'h_sal_gross_1'
  },
  {
    key: 'currency.freshness.realtime',
    namespace: 'fx',
    sourceTextEn: 'Live mid-market rate updated {minutes} min ago',
    description: 'Rate freshness indicator',
    context: 'Currency intelligence ticker',
    variables: ['minutes'],
    sensitivity: 'FINANCIAL',
    owner: 'FX Engine Team',
    sourceHash: 'h_fx_fresh_1'
  },
  {
    key: 'billing.cancel.confirm',
    namespace: 'billing',
    sourceTextEn: 'Are you sure you want to cancel your {tier} subscription? Your access remains active until {expiryDate}.',
    description: 'Subscription cancellation dialog confirmation message',
    context: 'Billing management modal',
    variables: ['tier', 'expiryDate'],
    sensitivity: 'LEGAL',
    owner: 'Billing & Compliance Team',
    sourceHash: 'h_bill_cancel_1'
  }
];

export const SAMPLE_TRANSLATION_UNITS: TranslationUnit[] = [
  // French translations
  { messageKey: 'nav.home', locale: 'fr-FR', translatedText: 'Accueil', status: 'PUBLISHED', sourceHashAtTranslation: 'h_nav_home_1', isOutdated: false, updatedAt: '2026-08-20', reviewer: 'Senior French Reviewer' },
  { messageKey: 'calculator.salary.title', locale: 'fr-FR', translatedText: 'Calculateur de Salaire Brut en Net', status: 'PUBLISHED', sourceHashAtTranslation: 'h_sal_title_1', isOutdated: false, updatedAt: '2026-08-20', reviewer: 'Financial Localization Lead' },
  { messageKey: 'calculator.salary.grossLabel', locale: 'fr-FR', translatedText: 'Salaire Brut Annuel', status: 'PUBLISHED', sourceHashAtTranslation: 'h_sal_gross_1', isOutdated: false, updatedAt: '2026-08-20', reviewer: 'Financial Localization Lead' },
  { messageKey: 'currency.freshness.realtime', locale: 'fr-FR', translatedText: 'Taux moyen du marché mis à jour il y a {minutes} min', status: 'PUBLISHED', sourceHashAtTranslation: 'h_fx_fresh_1', isOutdated: false, updatedAt: '2026-08-20' },
  { messageKey: 'billing.cancel.confirm', locale: 'fr-FR', translatedText: 'Êtes-vous sûr de vouloir résilier votre abonnement {tier} ? Votre accès restera actif jusqu\'au {expiryDate}.', status: 'PUBLISHED', sourceHashAtTranslation: 'h_bill_cancel_1', isOutdated: false, updatedAt: '2026-08-22', reviewer: 'Legal Counsel France' },

  // German translations
  { messageKey: 'nav.home', locale: 'de-DE', translatedText: 'Startseite', status: 'PUBLISHED', sourceHashAtTranslation: 'h_nav_home_1', isOutdated: false, updatedAt: '2026-08-15' },
  { messageKey: 'calculator.salary.title', locale: 'de-DE', translatedText: 'Brutto-Netto-Rechner für Deutschland', status: 'PUBLISHED', sourceHashAtTranslation: 'h_sal_title_1', isOutdated: false, updatedAt: '2026-08-15' },
  { messageKey: 'calculator.salary.grossLabel', locale: 'de-DE', translatedText: 'Bruttojahresgehalt', status: 'PUBLISHED', sourceHashAtTranslation: 'h_sal_gross_1', isOutdated: false, updatedAt: '2026-08-15' },

  // Arabic translations
  { messageKey: 'nav.home', locale: 'ar-SA', translatedText: 'الرئيسية', status: 'PUBLISHED', sourceHashAtTranslation: 'h_nav_home_1', isOutdated: false, updatedAt: '2026-08-23', reviewer: 'Senior Arabic Localization Lead' },
  { messageKey: 'calculator.salary.title', locale: 'ar-SA', translatedText: 'حاسبة الراتب وصافي الدخل', status: 'PUBLISHED', sourceHashAtTranslation: 'h_sal_title_1', isOutdated: false, updatedAt: '2026-08-23', reviewer: 'Senior Arabic Localization Lead' },
  { messageKey: 'calculator.salary.grossLabel', locale: 'ar-SA', translatedText: 'الراتب الإجمالي السنوي', status: 'PUBLISHED', sourceHashAtTranslation: 'h_sal_gross_1', isOutdated: false, updatedAt: '2026-08-23' },
  { messageKey: 'currency.freshness.realtime', locale: 'ar-SA', translatedText: 'سعر السوق المتوسط المباشر تم تحديثه منذ {minutes} دقيقة', status: 'REVIEWED', sourceHashAtTranslation: 'h_fx_fresh_1', isOutdated: false, updatedAt: '2026-08-24' }
];

export const TRANSLATION_MEMORY_STORE: TranslationMemoryEntry[] = [
  {
    id: 'TM-001',
    sourceHash: 'h_sal_gross_1',
    sourceTextEn: 'Gross Annual Pay',
    targetLocale: 'fr-FR',
    translatedText: 'Salaire Brut Annuel',
    domain: 'SALARY',
    qualityScore: 100,
    useCount: 142
  },
  {
    id: 'TM-002',
    sourceHash: 'h_sal_title_1',
    sourceTextEn: 'Salary & Take-Home Pay Calculator',
    targetLocale: 'fr-FR',
    translatedText: 'Calculateur de Salaire Brut en Net',
    domain: 'SALARY',
    qualityScore: 98,
    useCount: 89
  },
  {
    id: 'TM-003',
    sourceHash: 'h_sal_gross_1',
    sourceTextEn: 'Gross Annual Pay',
    targetLocale: 'ar-SA',
    translatedText: 'الراتب الإجمالي السنوي',
    domain: 'SALARY',
    qualityScore: 96,
    useCount: 38
  }
];

export const LOCALIZED_SEARCH_INTENTS: LocalizedSearchIntent[] = [
  {
    topicKey: 'salary-calculator',
    canonicalRoute: '/salary-engine',
    marketCode: 'US',
    locale: 'en-US',
    localizedSlug: 'paycheck-calculator',
    targetKeyword: 'paycheck calculator 2026',
    searchVolumeTier: 'HIGH',
    titleTag: 'Paycheck Calculator 2026 — Take-Home Pay & Federal Taxes | YamooPay',
    metaDescription: 'Calculate your exact paycheck take-home pay with 2026 federal IRS Pub 15-T and state tax brackets.',
    isIndexable: true,
    hreflangGroup: 'grp-salary'
  },
  {
    topicKey: 'salary-calculator',
    canonicalRoute: '/salary-engine',
    marketCode: 'GB',
    locale: 'en-GB',
    localizedSlug: 'take-home-pay-calculator',
    targetKeyword: 'salary calculator uk 2026',
    searchVolumeTier: 'HIGH',
    titleTag: 'UK Salary Calculator 2026 — PAYE & National Insurance Take-Home Pay | YamooPay',
    metaDescription: 'Accurate UK take-home salary calculator with 2026/27 HMRC PAYE tax bands, student loans and NI.',
    isIndexable: true,
    hreflangGroup: 'grp-salary'
  },
  {
    topicKey: 'salary-calculator',
    canonicalRoute: '/salary-engine',
    marketCode: 'FR',
    locale: 'fr-FR',
    localizedSlug: 'calcul-salaire-brut-en-net',
    targetKeyword: 'calcul salaire brut en net 2026',
    searchVolumeTier: 'HIGH',
    titleTag: 'Calcul Salaire Brut en Net 2026 — Cadre, Non-Cadre & Prélèvement à la Source | YamooPay',
    metaDescription: 'Convertissez votre salaire brut en net exact selon le barème officiel DGFiP 2026 avec cotisations URSSAF et PAS.',
    isIndexable: true,
    hreflangGroup: 'grp-salary'
  },
  {
    topicKey: 'salary-calculator',
    canonicalRoute: '/salary-engine',
    marketCode: 'CA',
    locale: 'fr-CA',
    localizedSlug: 'calculateur-salaire-net-quebec',
    targetKeyword: 'calculateur salaire quebec 2026',
    searchVolumeTier: 'MEDIUM',
    titleTag: 'Calculateur de Salaire Net Québec & Canada 2026 | YamooPay',
    metaDescription: 'Calculez votre paie nette avec les déductions fédérales ARC et provinciales Revenu Québec (RRQ, RQAP, AE).',
    isIndexable: true,
    hreflangGroup: 'grp-salary'
  },
  {
    topicKey: 'salary-calculator',
    canonicalRoute: '/salary-engine',
    marketCode: 'SA',
    locale: 'ar-SA',
    localizedSlug: 'salary-calculator-saudi-gosi',
    targetKeyword: 'حاسبة الرواتب والتأمينات الاجتماعية 2026',
    searchVolumeTier: 'HIGH',
    titleTag: 'حاسبة الراتب وصافي الدخل في السعودية 2026 مع خصم التأمينات (GOSI) | YamooPay',
    metaDescription: 'احسب صافي راتبك الشهري في المملكة العربية السعودية مع تطبيق استقطاعات التأمينات الاجتماعية وبدلات السكن.',
    isIndexable: true,
    hreflangGroup: 'grp-salary'
  }
];

export const MARKET_LAUNCH_CANDIDATES: MarketLaunchCandidate[] = [
  {
    id: 'LC-DE',
    marketCode: 'DE',
    targetLaunchWave: 'WAVE_2_EUROPE',
    opportunityScore: 92,
    dimensions: [
      { name: 'Search & Tool Demand', weight: 0.25, score: 95, rationale: 'Over 4.2M monthly searches for Brutto-Netto-Rechner' },
      { name: 'Financial Data & Rule Readiness', weight: 0.25, score: 94, rationale: 'BMF 2026 formula validated in Prompt 23' },
      { name: 'Localization & Content Coverage', weight: 0.20, score: 90, rationale: 'de-DE UI strings at 94% approval' },
      { name: 'Compliance & GDPR Readiness', weight: 0.15, score: 96, rationale: 'EU GDPR / TCF 2.2 fully certified' },
      { name: 'Payment & Monetization Readiness', weight: 0.15, score: 85, rationale: 'SEPA & Giropay ready; Paid mode staged' }
    ],
    currentStage: 'ACTIVE_FREE',
    gatesPassed: {
      countryPackValid: true,
      localeUxPassed: true,
      financialDataPassed: true,
      compliancePassed: true,
      accessibilityPassed: true,
      qualityCertified: true,
      paymentReadyIfPaid: false
    },
    blockers: [],
    targetFreeLaunchDate: '2026-08-01',
    targetPaidLaunchDate: '2026-10-15'
  },
  {
    id: 'LC-SA',
    marketCode: 'SA',
    targetLaunchWave: 'WAVE_4_ME_LATAM',
    opportunityScore: 88,
    dimensions: [
      { name: 'Search & Tool Demand', weight: 0.25, score: 90, rationale: 'Surging fintech calculator queries in GCC region' },
      { name: 'Financial Data & Rule Readiness', weight: 0.25, score: 86, rationale: 'GOSI salary engine validated' },
      { name: 'Localization & RTL UX', weight: 0.20, score: 92, rationale: 'Arabic typography & RTL engine passing visual QA' },
      { name: 'Compliance & PDPL Readiness', weight: 0.15, score: 82, rationale: 'Saudi Personal Data Protection Law review ongoing' },
      { name: 'Payment & Mada Readiness', weight: 0.15, score: 80, rationale: 'Mada integration in progress' }
    ],
    currentStage: 'LIMITED_ROLLOUT',
    gatesPassed: {
      countryPackValid: true,
      localeUxPassed: true,
      financialDataPassed: true,
      compliancePassed: false,
      accessibilityPassed: true,
      qualityCertified: false,
      paymentReadyIfPaid: false
    },
    blockers: ['Awaiting final PDPL privacy policy signoff from Legal Counsel'],
    targetFreeLaunchDate: '2026-09-01',
    targetPaidLaunchDate: '2026-11-01'
  },
  {
    id: 'LC-JP',
    marketCode: 'JP',
    targetLaunchWave: 'WAVE_3_APAC',
    opportunityScore: 82,
    dimensions: [
      { name: 'Search & Tool Demand', weight: 0.25, score: 88, rationale: 'High salary deduction and currency volatility interest' },
      { name: 'Financial Data & Rule Readiness', weight: 0.25, score: 75, rationale: 'National tax withholding rules in translation' },
      { name: 'Localization & Content Coverage', weight: 0.20, score: 78, rationale: 'ja-JP terminology under review' },
      { name: 'Compliance & APPI Readiness', weight: 0.15, score: 90, rationale: 'APPI consent framework ready' },
      { name: 'Payment Readiness', weight: 0.15, score: 84, rationale: 'Konbini and JCB gateway requirements identified' }
    ],
    currentStage: 'LOCALIZATION',
    gatesPassed: {
      countryPackValid: true,
      localeUxPassed: false,
      financialDataPassed: false,
      compliancePassed: true,
      accessibilityPassed: false,
      qualityCertified: false,
      paymentReadyIfPaid: false
    },
    blockers: ['Salary tax bracket verification for 2026 Reiwa 8 tax reform'],
    targetFreeLaunchDate: '2026-11-15'
  }
];

export const SAMPLE_LOCALIZATION_INCIDENTS: MarketLocalizationIncident[] = [
  {
    id: 'INC-I18N-041',
    marketCode: 'CA',
    locale: 'fr-CA',
    type: 'LOCALE_FALLBACK',
    severity: 'LOW',
    description: 'Correctly prevented French Canadian tax query from falling back to France DGFiP rule; showed verified Quebec TP-1 in Canadian French.',
    status: 'RESOLVED',
    reportedAt: '2026-08-21 09:14:00',
    killSwitchActive: false
  },
  {
    id: 'INC-I18N-042',
    marketCode: 'SA',
    locale: 'ar-SA',
    type: 'RTL_BROKEN',
    severity: 'MEDIUM',
    description: 'Fixed mixed-direction currency badge rendering (e.g. SAR 500 in Arabic text block) with Unicode bidi isolation tags.',
    status: 'RESOLVED',
    reportedAt: '2026-08-23 14:30:00',
    killSwitchActive: false
  }
];
